import type {
  QuestionnaireItemAnswerOptionWithTranslations,
  QuestionnaireItemWithTranslations,
  QuestionnaireWithTranslations,
} from '@liza/fhir-utils';
import {
  buildScoreObservationTemplate,
  buildTemplateExtractExtension,
  frequencyAnswerOption,
  SEMVER_VERSION_ALGORITHM_EXTENSION,
  symptomItem,
  withEs,
} from './scoredSurvey';

/**
 * IPSS (International Prostate Symptom Score) — the AUA Symptom Index for lower urinary tract
 * symptoms. Item structure and LOINC coding follow the LOINC IPSS panel (LOINC 80976-4), published
 * here under our own canonical `url` and version, mirroring {@link ./gad7} — they share the
 * scored-survey machinery in {@link ./scoredSurvey}.
 *
 * The seven symptom items are required and scored 0–5; the total (0–35) is derived server-side via
 * Medplum's SDC `QuestionnaireResponse/$extract` (a contained Observation template carrying the
 * total-score LOINC plus `sdc-questionnaire-templateExtract` annotations). Behavior is verified by
 * `src/__tests__/liveMedplum/ipssExtractScoring.test.ts`.
 *
 * Unlike PHQ-9/GAD-7, IPSS carries an eighth item — the quality-of-life question (LOINC 81090-3) on
 * a separate 0–6 "Delighted → Terrible" scale. It is required and collected, but is NOT part of the
 * symptom total: it is excluded simply by being left out of `scoredSymptomItems` (the only items
 * `buildScoreObservationTemplate` sums), so the QoL answer never enters the extract expression. The
 * first six symptom items share the standard 0–5 frequency scale; the nocturia item (80890-7) uses
 * its own 0–5 count scale ("None" → "5 or more times"), and the QoL item its own 0–6 scale.
 *
 * Source: the IPSS FHIR survey JSON attached to the originating issue (English + Spanish), whose
 * item structure and LOINC coding match the LOINC IPSS panel (https://loinc.org/80976-4). The
 * English item text is the standard validated IPSS wording; the inline Spanish translations on the
 * `_`-sibling elements are transcribed from the Spanish source JSON. Because IPSS is a validated
 * instrument, these strings must be reviewed against the official bilingual IPSS before go-live and
 * never free-translated; scoring validity depends on the exact wording. IPSS is based on the
 * American Urological Association (AUA) Symptom Index.
 *
 * Each item keeps its full "Over the past month" wording inline rather than lifting the timeframe
 * into a `description` framing statement (the v3 pattern PHQ-9/GAD-7 use): the symptom items and the
 * QoL item have no common stem, so there is no single framing statement that fits all eight.
 */

/** IPSS total symptom score. Distinct from the panel LOINC (80976-4) on `Questionnaire.code`. */
export const IPSS_TOTAL_SCORE_LOINC = '80891-5';

/** Standard 0–5 frequency scale shared by the first six symptom items. */
const frequencyAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  frequencyAnswerOption('0', 0, 'LA6568-5', 'Not at all', 'Ninguna'),
  frequencyAnswerOption('1', 1, 'LA24478-2', 'Less than 1 time in 5', 'Menos de 1 vez cada 5 veces'),
  frequencyAnswerOption('2', 2, 'LA14723-3', 'Less than half the time', 'Menos de la mitad de las veces'),
  frequencyAnswerOption('3', 3, 'LA24963-3', 'About half the time', 'Aproximadamente la mitad de las veces'),
  frequencyAnswerOption('4', 4, 'LA14724-1', 'More than half the time', 'Más de la mitad de las veces'),
  frequencyAnswerOption('5', 5, 'LA13865-3', 'Almost always', 'Casi siempre'),
];

/** Nocturia count scale (item 80890-7) — 0–5 but with "times per night" answers, not frequencies. */
const nocturiaAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  frequencyAnswerOption('0', 0, 'LA137-2', 'None', 'Ninguna'),
  frequencyAnswerOption('1', 1, 'LA14677-1', '1 time', '1 vez'),
  frequencyAnswerOption('2', 2, 'LA23711-7', '2 times', '2 veces'),
  frequencyAnswerOption('3', 3, 'LA23712-5', '3 times', '3 veces'),
  frequencyAnswerOption('4', 4, 'LA26056-4', '4 times', '4 veces'),
  frequencyAnswerOption('5', 5, 'LA26057-2', '5 or more times', '5 o más veces'),
];

/** Quality-of-life scale (item 81090-3) — 0–6 "Delighted → Terrible". Collected but not scored. */
const qualityOfLifeAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  frequencyAnswerOption('0', 0, 'LA16674-6', 'Delighted', 'Encantado'),
  frequencyAnswerOption('1', 1, 'LA24973-2', 'Pleased', 'Muy satisfecho'),
  frequencyAnswerOption('2', 2, 'LA24974-0', 'Mostly satisfied', 'Más bien satisfecho'),
  frequencyAnswerOption(
    '3',
    3,
    'LA24975-7',
    'Mixed (about equally satisfied and dissatisfied)',
    'Tan satisfecho como insatisfecho',
  ),
  frequencyAnswerOption('4', 4, 'LA24976-5', 'Mostly dissatisfied', 'Más bien insatisfecho'),
  frequencyAnswerOption('5', 5, 'LA24977-3', 'Unhappy', 'Muy insatisfecho'),
  frequencyAnswerOption('6', 6, 'LA24978-1', 'Terrible', 'Fatal'),
];

const scoredSymptomItems: QuestionnaireItemWithTranslations[] = [
  symptomItem(
    '/80884-0',
    '80884-0',
    'Over the past month, how often have you had a sensation of not emptying your bladder completely after you finished urinating?',
    'Over the past month, how often have you had a sensation of not emptying your bladder completely after you finished urinating?',
    'Durante más o menos los últimos 30 días, ¿cuántas veces ha tenido la sensación de no vaciar completamente la vejiga al terminar de orinar?',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/80885-7',
    '80885-7',
    'Over the past month, how often have you had to urinate again less than two hours after you finished urinating?',
    'Over the past month, how often have you had to urinate again less than two hours after you finished urinating?',
    'Durante más o menos los últimos 30 días, ¿cuántas veces ha tenido que volver a orinar a las dos horas siguientes después de haber orinado?',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/80886-5',
    '80886-5',
    'Over the past month, how often have you found you stopped and started again several times when you urinated?',
    'Over the past month, how often have you found you stopped and started again several times when you urinated?',
    'Durante más o menos los últimos 30 días, ¿cuántas veces ha notado que, al orinar, paraba y comenzaba de nuevo varias veces?',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/80887-3',
    '80887-3',
    'Over the past month, how often have you found it difficult to postpone urination?',
    'Over the past month, how often have you found it difficult to postpone urination?',
    'Durante más o menos los últimos 30 días, ¿cuántas veces ha tenido dificultad para aguantarse las ganas de orinar?',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/80888-1',
    '80888-1',
    'Over the past month, how often have you had a weak urinary stream?',
    'Over the past month, how often have you had a weak urinary stream?',
    'Durante más o menos los últimos 30 días, ¿cuántas veces ha observado que el flujo de orina es poco fuerte?',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/80889-9',
    '80889-9',
    'Over the past month, how often have you had to push or strain to begin urination?',
    'Over the past month, how often have you had to push or strain to begin urination?',
    'Durante más o menos los últimos 30 días, ¿cuántas veces ha tenido que apretar o hacer fuerza para comenzar a orinar?',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/80890-7',
    '80890-7',
    'Over the past month, how many times did you most typically get up to urinate from the time you went to bed at night until the time you got up in the morning?',
    'Over the past month, how many times did you most typically get up to urinate from the time you went to bed at night until the time you got up in the morning?',
    'Durante más o menos los últimos 30 días, ¿cuántas veces suele tener que levantarse para orinar desde que se va a la cama por la noche hasta que se levanta por la mañana?',
    true,
    nocturiaAnswerOptions,
  ),
];

/**
 * Quality-of-life item (LOINC 81090-3). Required and collected, but deliberately omitted from
 * `scoredSymptomItems` so it never enters the total-score extract sum (see file doc).
 */
const qualityOfLifeItem: QuestionnaireItemWithTranslations = symptomItem(
  '/81090-3',
  '81090-3',
  'If you were to spend the rest of your life with your urinary condition just the way it is now, how would you feel about that?',
  'If you were to spend the rest of your life with your urinary condition just the way it is now, how would you feel about that?',
  '¿Cómo se sentiría si tuviera que pasar el resto de la vida con los síntomas prostáticos tal y como los tiene ahora?',
  true,
  qualityOfLifeAnswerOptions,
);

const ipssScoreObservationTemplate = buildScoreObservationTemplate({
  id: 'ipss-score-tmpl',
  totalScoreCode: IPSS_TOTAL_SCORE_LOINC,
  display: 'IPSS',
  scoredItems: scoredSymptomItems,
});

export const ipssQuestionnaire: QuestionnaireWithTranslations = {
  resourceType: 'Questionnaire',
  meta: {
    profile: ['http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire'],
  },
  extension: [SEMVER_VERSION_ALGORITHM_EXTENSION, buildTemplateExtractExtension('ipss-score-tmpl')],
  contained: [ipssScoreObservationTemplate],
  url: 'https://liza.health/fhir/questionnaire/ipss',
  identifier: [{ system: 'urn:ietf:rfc:3986', value: 'https://liza.health/fhir/questionnaire/ipss' }],
  version: '1',
  name: 'IPSS',
  language: 'en',
  title: 'IPSS',
  _title: withEs('Escala internacional de síntomas prostáticos (IPSS)'),
  status: 'active',
  subjectType: ['Patient'],
  date: '2026-06-26',
  publisher: 'Liza Health',
  jurisdiction: [{ coding: [{ system: 'urn:iso:std:iso:3166', code: 'US' }] }],
  copyright:
    'This material contains content from LOINC® (http://loinc.org). LOINC is copyright © 1995-2024, Regenstrief Institute, Inc. and the LOINC Committee, and is available at no cost under the license at http://loinc.org/license. The International Prostate Symptom Score (IPSS) is based on the American Urological Association (AUA) Symptom Index, © American Urological Association.',
  code: [{ system: 'http://loinc.org', code: '80976-4', display: 'International Prostate Symptom Score [IPSS]' }],
  item: [...scoredSymptomItems, qualityOfLifeItem],
};
